// MindForge web — TypeScript port of logic/nback.py.
//
// N-Back: letter stimuli arrive one at a time. The player presses Match
// whenever the current letter equals the one shown N steps ago.

const DEFAULT_POOL = "ABCDEFGH";

export interface NBackScore {
  hits: number;
  misses: number;
  falseAlarms: number;
  correctRejects: number;
  total: number;
  accuracyPct: number;
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Generate a letter sequence with a controlled proportion of n-back matches.
 *
 * Defensive clamps match the Python reference:
 *   - n < 1 → coerced to 1
 *   - pool unset or fewer than 2 chars → reset to the default 8-letter pool
 */
export function generateNBackSequence(
  length: number,
  n: number,
  targetRatio = 0.3,
  pool?: string,
): string[] {
  if (length <= 0) return [];
  const effectiveN = n < 1 ? 1 : n;
  const effectivePool =
    !pool || pool.length < 2 ? DEFAULT_POOL : pool;
  const letters = effectivePool.split("");

  const seq: string[] = [];
  for (let i = 0; i < length; i++) {
    if (i >= effectiveN && Math.random() < targetRatio) {
      // Force a target — repeat the stimulus from N steps back.
      seq.push(seq[i - effectiveN]);
    } else {
      // Non-target — pick a letter that does NOT match the n-back position.
      let choices = letters;
      if (i >= effectiveN) {
        const forbidden = seq[i - effectiveN];
        choices = letters.filter((c) => c !== forbidden);
      }
      seq.push(randChoice(choices));
    }
  }
  return seq;
}

/** Boolean mask: true at positions that are n-back matches. */
export function getNBackTargets(sequence: string[], n: number): boolean[] {
  return sequence.map(
    (_, i) => i >= n && sequence[i] === sequence[i - n],
  );
}

/** Score a completed n-back run. */
export function scoreNBack(
  sequence: string[],
  n: number,
  responses: boolean[],
): NBackScore {
  const targets = getNBackTargets(sequence, n);
  let hits = 0;
  let misses = 0;
  let falseAlarms = 0;
  let correctRejects = 0;

  for (let i = 0; i < sequence.length; i++) {
    const isTarget = targets[i];
    const responded = i < responses.length ? responses[i] : false;
    if (isTarget && responded) hits += 1;
    else if (isTarget && !responded) misses += 1;
    else if (!isTarget && responded) falseAlarms += 1;
    else correctRejects += 1;
  }

  const total = sequence.length;
  const correct = hits + correctRejects;
  const accuracyPct = Math.trunc(
    (correct / Math.max(1, total)) * 100,
  );

  return {
    hits,
    misses,
    falseAlarms,
    correctRejects,
    total,
    accuracyPct,
  };
}

/**
 * Compute the next n-back level based on round accuracy.
 *
 *   ≥ 80% → increase N (capped at maxN)
 *   < 50% → decrease N (floored at 1)
 *   else  → stay
 */
export function adaptiveN(
  currentN: number,
  accuracyPct: number,
  maxN = 8,
): number {
  if (accuracyPct >= 80 && currentN < maxN) return currentN + 1;
  if (accuracyPct < 50 && currentN > 1) return currentN - 1;
  return currentN;
}
